import { AfterContentInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@devinforius/i-essentials';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TreeSelect } from 'primeng/treeselect';
import { forkJoin } from 'rxjs';
import { ClassificationCode } from 'src/app/shared/models/classement/classification-codes';
import { Service } from 'src/app/shared/models/iCourrier/service';
import { BinderManagementDTO, ClassificationCodeDto } from 'src/app/shared/interfaces';
import { ClassificationsService } from 'src/app/shared/services/classifications.service';
import * as _ from 'underscore';

@Component({
  selector: 'app-create-edit-plastic-liner',
  templateUrl: './create-edit-plastic-liner.component.html',
  styleUrls: ['./create-edit-plastic-liner.component.scss'],
})
export class CreateEditPlasticLinerComponent implements OnInit, AfterContentInit {
  @ViewChild('treeSelectCreatePlasticLiner') treeSelectCreatePlasticLiner: TreeSelect;

  @Input() public selectedRowData = null;
  @Input() public servicesParentSingleSelection: Service[] = [];
  @Input() public servicesFusionned = [];
  @Input() public formDatasToSendBackToCreatePlasticLiner;

  @Output() closeCreateEditPlasticLiner: EventEmitter<{
    form?: FormGroup;
    selectedRowData?;
    createdPlasticLiner?: BinderManagementDTO;
    reload?: boolean;
  }> = new EventEmitter();

  public translationPlaceholderPTree = '';
  public classementsLV: ClassificationCode[] = [];
  public selectedOptionCreatePlasticLiner = null;

  public loaded = false;
  public plasticLiner = null;

  public formCreateEditPlasticLiner = new UntypedFormGroup({
    fardeId: new FormControl(null, Validators.required),
    classementId: new FormControl(null, Validators.required),
    serviceId: new FormControl(null, Validators.required),
    description: new FormControl(null, Validators.required),
    localisation: new FormControl(null),
    remarques: new FormControl(null),
    serviceIds: new FormControl(null),
    classementAffine: new FormControl(null),
    accesRestreint: new FormControl(null),
    archive: new FormControl(false),
  });

  constructor(
    private translater: TranslateService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private classificationsService: ClassificationsService
  ) {}

  ngOnInit(): void {
    this.translationPlaceholderPTree = this.translater.translate('pleaseSelectAService');
  }
  ngAfterContentInit(): void {
    this.spinner.show();
    this.setDatasFromConfig();
  }

  setDatasFromConfig(): void {
    //on arrive ici dans tous les cas (édition et création). Pour l'édition on remplacera ces données plus loin (c.f. setDatasForPatchValues() )
    // => pas de panique si jusqu'ici si la fardeId n'est pas celle que vous pensez :) Il s'agit simplement de la donnée quand on clique sur la farde pour créer une chemise
    // En effet quand on clique sur nouvelle chemise : this.selectedRowData = la farde
    // mais quand on vient en édition : selectedRowData = la chemise
    // Dans les deux cas la récup de la fardeId est !==
    this.formCreateEditPlasticLiner.patchValue({
      fardeId: this.selectedRowData.node.data.id,
      classementId: this.selectedRowData.node.data.classementId,
      serviceId: this.selectedRowData.node.data.serviceId,
    });
    if (this.selectedRowData.node.data.serviceId) {
      this.patchService(this.selectedRowData.node.data.serviceId);
    }

    forkJoin({
      classements: this.classificationsService.fetchClassificationCodes(),
    }).subscribe({
      next: (res) => {
        this.formatClassements(res.classements);
        //Si on crée plusieurs chemises ou si on édite alors on va re-patcher les bonnes données
        if (this.formDatasToSendBackToCreatePlasticLiner?.form) {
          this.setDatasForPatchValues();
        } else {
          this.loaded = true;
          this.spinner.hide();
        }
      },
    });
  }

  formatClassements(classements: ClassificationCodeDto[]): void {
    this.classementsLV = classements.map((classement) => {
      const newClassification = new ClassificationCode(classement);
      newClassification.folderNameAndCode = (classement.code ? classement.code + ' - ' : '') + classement?.description;
      return newClassification;
    });
  }

  setDatasForPatchValues(): void {
    // La chemise sera soit le formulaire renvoyé lors d'une création multiple (création), soit la chemise à modifier: ce sur quoi le user a cliqué : selectedRowData (modification)
    this.plasticLiner = this.formDatasToSendBackToCreatePlasticLiner?.form;

    // pour avoir la farde on doit faire attention à ce que l'on veut faire avec la chemise
    // Si on modifie la chemise, on a cliqué SUR LA CHEMISE => this.selectedRowData.parent.data.id
    // Si on en crée une nouvelle alors on a clique SUR LA FARDE => this.selectedRowData.node.data.id
    const fardeId = this.selectedRowData.node.data.id;

    this.formCreateEditPlasticLiner.patchValue({
      fardeId: fardeId,
      classementId: this.plasticLiner.classementId ?? null,
      serviceId: this.plasticLiner.serviceId ?? null,
      localisation: this.plasticLiner.localisation ?? null,
    });

    //puis on va setter les données obligatoires pour l'affichage du service
    if (this.plasticLiner.serviceId) {
      this.patchService(this.plasticLiner.serviceId);
    }
    this.loaded = true;
    this.spinner.hide();
  }

  private patchService(serviceId): void {
    this.selectedOptionCreatePlasticLiner = _.first(this.servicesFusionned.filter((x) => x.value === serviceId));
  }

  select(event): void {
    this.formCreateEditPlasticLiner.patchValue({
      serviceId: event.node.value,
    });
  }

  createPlasticLiner(type: 'single' | 'multiple'): void {
    if (this.formCreateEditPlasticLiner.valid) {
      this.spinner.show();
      this.classificationsService.postBinder(this.formCreateEditPlasticLiner.value).subscribe({
        next: (res: BinderManagementDTO) => {
          this.toastr.success(this.translater.translate('classification.successfullyCreatedPlasticLiner'));
          type === 'single' ? this.closeModalCreatePlasticLiner(true, res) : this.resetForCreationMultipleBinders(res);
          this.spinner.hide();
        },
        error: (err) => {
          console.error(err);
          this.toastr.error(this.translater.translate('error.errorOccurredWhilePostingFolder'));
        },
      });
    } else {
      this.toastr.warning(this.translater.translate('classification.requiredFieldsNotFilled'));
    }
  }

  resetForCreationMultipleBinders(createdPlasticLiner?: BinderManagementDTO): void {
    this.closeCreateEditPlasticLiner.emit({
      form: Object.assign({}, this.formCreateEditPlasticLiner.value),
      selectedRowData: this.selectedRowData,
      createdPlasticLiner: createdPlasticLiner,
    });
  }

  public closeModalCreatePlasticLiner(reload, createdPlasticLiner?): void {
    this.closeCreateEditPlasticLiner.emit({ reload: reload, createdPlasticLiner: createdPlasticLiner });
  }
}
